/**
 * Lazy column-list resolver for INSERT inlay hints.
 *
 * Used by `insertInlayHintsProvider` when an `INSERT INTO schema.table VALUES (...)`
 * statement has no explicit column list and the engine (e.g. MSSQL) has no
 * local schema cache. Resolves columns via the existing `getTableMetadata` IPC
 * and caches the result in-memory with a 5-minute TTL.
 *
 * Lives outside the engine-specific schemaCache files because:
 *  - The inlay hints provider is called from Monaco, not from React, so a
 *    module-level `Map` is the right shape (TanStack Query caches are
 *    React-hook-scoped).
 *  - It's engine-agnostic: any engine that reaches this path goes through the
 *    same IPC and gets the same cache behavior.
 */

import type { TableMetadataResult } from '../../../../types/electron';

export type ColumnResolverParams = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
};

type CacheEntry = {
  columns: string[];
  fetchedAt: number;
};

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<string[] | null>>();

const TTL_MS = 5 * 60 * 1000; // 5 minutes — matches TanStack Query default for getTableMetadata

/**
 * Build the cache key for a (connection, database, schema, table) tuple.
 * Table names are lowercased (most SQL engines are case-insensitive for object
 * lookups); schema names are kept case-sensitive.
 */
export function buildCacheKey(params: ColumnResolverParams): string {
  return `${params.connectionId}::${params.database}::${params.schema}::${params.table.toLowerCase()}`;
}

function isFresh(entry: CacheEntry): boolean {
  return Date.now() - entry.fetchedAt < TTL_MS;
}

/**
 * Synchronously returns cached column names, or `undefined` if the cache is
 * cold or stale. Does NOT trigger a fetch.
 */
export function getCachedColumns(params: ColumnResolverParams): string[] | undefined {
  const key = buildCacheKey(params);
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (!isFresh(entry)) {
    cache.delete(key);
    return undefined;
  }
  return entry.columns;
}

/**
 * Fetch the column list for a table via `getTableMetadata`. Caches the result
 * on success. Returns `null` on any failure so callers can distinguish "no
 * data" from an empty-but-valid table (which should not occur in practice but
 * is handled defensively).
 *
 * Concurrent calls for the same cache key share a single IPC round-trip.
 */
export async function fetchColumns(params: ColumnResolverParams): Promise<string[] | null> {
  const key = buildCacheKey(params);

  // Serve from cache if fresh.
  const cached = cache.get(key);
  if (cached && isFresh(cached)) {
    return cached.columns;
  }

  // Join an in-flight request if present.
  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = (async (): Promise<string[] | null> => {
    try {
      const api = (globalThis as { window?: { electronAPI?: { getTableMetadata?: (r: unknown) => Promise<TableMetadataResult> } } }).window?.electronAPI;
      if (!api?.getTableMetadata) {
        return null;
      }

      const result = await api.getTableMetadata({
        connectionId: params.connectionId,
        database: params.database,
        schema: params.schema,
        tableName: params.table,
      });

      if (!result?.success || !result.metadata?.columns) {
        return null;
      }

      const columns = result.metadata.columns.map((c) => c.name).filter((n): n is string => !!n);
      cache.set(key, { columns, fetchedAt: Date.now() });
      return columns;
    } catch {
      // Swallow IPC errors — inlay hints are a best-effort UX nicety, not a
      // load-bearing feature. Returning null lets the provider return empty
      // hints without surfacing a toast.
      return null;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
}

/**
 * Clear all cached column lists for a given connection. Call on disconnect or
 * when the schema is known to have changed.
 */
export function invalidateColumns(connectionId: string): void {
  const prefix = `${connectionId}::`;
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

/**
 * Test-only: clears the entire cache and any in-flight promises so unit tests
 * can run in isolation. Not exported from the barrel file.
 */
export function __resetColumnResolverForTest(): void {
  cache.clear();
  inFlight.clear();
}
